import type {WatchCallback, WatchOptions, WatchSource} from 'vue';
import {defaults, isEqual} from 'lodash-es';
import {v4 as uuidv4} from 'uuid';
import type {ISbComponentType} from 'storyblok-js-client';
import {extractRichtextPlainText} from './richtextPlainText';
import {ProductionTypeIdEnum} from '~/stores/shoppingCart';
import useTransformer from '~/composables/useTransformer';
import {ApiError, CancelablePromise} from '~/openapi';
import {REQUEST_TIMEOUT_IN_SECONDS} from '#shared/constants';
import {
	dropUnderscoreRouteParts,
	ensureLeadingSlash,
	removeTrailingSlash,
} from '#shared/utils/path';
import {TimeoutError} from '@/utils/TimeoutError';
import {isEmptyOrFalsy} from '@/utils/isEmptyOrFalsy';
import type {AnchorStoryblok, AssetStoryblok, RichtextStoryblok} from '~/storyblok/types';
import type {ISbDatasourceEntry} from '~/types/storyblok';
import type {Option} from '@/types/formkitSelectbox';
import type {Arrayable, Awaitable, Tree} from '@/types/utils';
import type {IMapBounds, IMapCoords} from '~/types/map';
import type {Gsoa_ProductCatalog_ProductDetail as ProductDetail} from '~/openapi';
import {ProductAvailability} from '~/utils/productAvailability';
import type {Hypermarket, SimplifiedHypermarket} from '~/types/hypermarket';

export interface Tag {
	id: number;
	label: string;
	value: string;
}

interface RetryOptions {
	maxTries?: number;
	shouldGiveUp?: (e: unknown) => Awaitable<boolean>;
}

interface CancelTimeoutOptions {
	ms?: number;
	retry?: boolean | number;
}

/**
	Normalizes a string by converting it to uppercase, applying Unicode normalization form D (NFD), and removing diacritics.
	@param str The string to normalize.
	@returns The normalized string.
 */
export function normalizeString(str: string): string {
	return str.toUpperCase().normalize('NFD');
}

export function mod(n: number, modulo: number): number {
	return ((n % modulo) + modulo) % modulo;
}

export function humanToComputerIndexing(n: number): number {
	return n - 1;
}

export function computerToHumanIndexing(n: number): number {
	return n + 1;
}

export function arrayify<T>(val: Arrayable<T>, truncateEmpty = false): T[] {
	return Array.isArray(val) ? val : truncateEmpty && isEmptyOrFalsy(val) ? [] : [val];
}

export function abortController() {
	const controller = new AbortController();

	return {
		signal: controller.signal,
		abort: (reason?: any) => {
			controller.abort(reason);
		},
	};
}

export function isErrorCausedBy<T>(error: Error, causeType: {new (): T}): boolean {
	return (
		error instanceof causeType
		|| (error.cause instanceof Error && isErrorCausedBy(error.cause, causeType))
	);
}

export function checkErrorDeep(error: unknown, predicate: (error: Error) => boolean): boolean {
	return !!error && error instanceof Error && (predicate(error) || checkErrorDeep(error.cause, predicate));
}

export function checkApiErrorStatus4XXDeep(error: unknown): boolean {
	return checkErrorDeep(error, (e) => e instanceof ApiError && e.status >= 400 && e.status < 500);
}

export function checkApiErrorStatus5XXDeep(error: unknown): boolean {
	return checkErrorDeep(error, (e) => e instanceof ApiError && e.status >= 500);
}

export async function retry<T>(handler: () => Promise<T>, maxTries?: number): Promise<T>;
export async function retry<T>(handler: () => Promise<T>, options?: RetryOptions): Promise<T>;
export async function retry<T>(
	handler: () => Promise<T>,
	optionsOrMaxTries?: RetryOptions | number,
): Promise<T> {
	const normalizedOpts: RetryOptions
		= typeof optionsOrMaxTries === 'number' ? {maxTries: optionsOrMaxTries} : optionsOrMaxTries ?? {};
	const opts: Required<RetryOptions> = defaults({}, normalizedOpts, {
		maxTries: 3,
		shouldGiveUp: () => false,
	});

	let tries = 0;

	while (true) {
		try {
			return await handler();
		} catch (e: unknown) {
			if (++tries >= opts.maxTries || await opts.shouldGiveUp(e)) {
				throw e;
			}
		}
	}
}

export function cancelTimeout<T>(handler: () => CancelablePromise<T>, ms?: number): CancelablePromise<T>;
export function cancelTimeout<T>(handler: () => CancelablePromise<T>, options: CancelTimeoutOptions): CancelablePromise<T>;
export function cancelTimeout<T>(handler: () => Promise<T>, ms?: number): Promise<T>;
export function cancelTimeout<T>(handler: () => Promise<T>, options: CancelTimeoutOptions): Promise<T>;
export function cancelTimeout<T>(
	handler: () => Promise<T> | CancelablePromise<T>,
	optionsOrMs?: CancelTimeoutOptions | number,
): Promise<T> | CancelablePromise<T> {
	const normalizedOpts = typeof optionsOrMs === 'number' ? {ms: optionsOrMs} : optionsOrMs ?? {};
	const opts: Required<CancelTimeoutOptions> = defaults({}, normalizedOpts, {
		retry: 1,
		ms: REQUEST_TIMEOUT_IN_SECONDS * 1000,
	});

	return retry(
		async () => {
			let timeout;
			const promise = handler();

			if (!import.meta.client && promise instanceof CancelablePromise) {
				timeout = setTimeout(() => {
					promise.cancel(new TimeoutError(opts.ms));
				}, opts.ms);
			}

			try {
				return await promise;
			} finally {
				clearTimeout(timeout);
			}
		},
		{
			maxTries: opts.retry === true ? 3 : Number(opts.retry),
			// Give up when handler fails with something other than timing out
			shouldGiveUp: (e: unknown) => !(e instanceof Error && isErrorCausedBy(e, TimeoutError)),
		},
	);
}

// This function will return the previously returned promise when called again with the same arguments
// When called with different arguments, the previous promise will be cancelled and a new one will be created
export function dedupePromise<P, R>(
	fn: (params: P, options: {signal: AbortSignal}) => CancelablePromise<R> | Promise<R>,
): (params?: P) => CancelablePromise<R> | Promise<R> {
	let promise: CancelablePromise<R> | Promise<R> | null = null;
	let abortController: AbortController | null = null;
	let lastParams: {value: P | undefined} | null = null;

	return (params) => {
		if (lastParams && !isEqual(params, lastParams.value)) {
			lastParams = {value: params};

			if (promise instanceof CancelablePromise) {
				promise.cancel();
			} else {
				abortController?.abort();
			}
			promise = null;
		}

		if (!promise) {
			abortController = new AbortController();
			promise = fn(params as P, {signal: abortController.signal});

			promise.finally(() => {
				promise = null;
			});
		}

		return promise;
	};
}

export function useReadingTime(content: RichtextStoryblok | undefined, additionalContent?: string): number {
	const plainContentText = extractRichtextPlainText(content);
	const plainAdditionalContent = additionalContent ? stripRichtextTags(additionalContent) : '';
	const totalWordCount = countWords(plainContentText + plainAdditionalContent);

	return estimateReadingTime(totalWordCount);
}

function stripRichtextTags(input: string): string {
	const jsonTextMatches = input.match(/"text":"(.*?)"/g) || [];
	const extractedJsonTexts = jsonTextMatches
		.map((item) => {
			const match = item.match(/"text":"(.*?)"/);
			return match ? match[1] : '';
		})
		.join(' ');

	const strippedHtmlText = input.replace(/<[^>]*>/g, ' ');

	return extractedJsonTexts + strippedHtmlText;
}

function countWords(input: string): number {
	return input.split(/\s+/).filter((word) => word.length > 0).length;
}

function estimateReadingTime(wordCount: number, wordsPerMinute = 200): number {
	return Math.ceil(wordCount / wordsPerMinute);
}

export function extractAssetInfo(asset: AssetStoryblok) {
	const [name, extension] = asset.filename.match('([^/]+$)')![0].split('.');

	return {
		name: asset.title || name,
		extension,
	};
}

export function mapWithAccumulator<Acc, In extends Acc, Out>(
	array: In[],
	mapFn: (acc: Acc, item: In, index: number, arr: In[]) => Out,
	reduceFn: (acc: Acc, item: In, index: number, arr: In[]) => Acc,
	start?: Acc,
): Out[] {
	let acc: Acc = start === undefined ? array[0] : start;

	return array.map((...mapArgs) => {
		const mapResult = mapFn(acc, ...mapArgs);
		acc = reduceFn(acc, ...mapArgs);
		return mapResult;
	});
}

export function mapWithPreviousMapped<In, Out>(
	array: In[],
	mapFn: (prev: Out, item: In, index: number, arr: In[]) => Out,
	start: Out,
): Out[];
export function mapWithPreviousMapped<In, Out>(
	array: In[],
	mapFn: (prev: Out | undefined, item: In, index: number, arr: In[]) => Out,
	start?: Out,
): Out[] {
	let prev: Out | undefined = start;

	return array.map((item, index, arr) => {
		prev = mapFn(prev, item, index, arr);
		return prev;
	});
}

// Works just like _.chunk, but with a predicate
export function chunkBy<T>(arr: T[], predicate: (a: T, b: T) => boolean): T[][] {
	let group = [];
	let lastItem: T;
	let currentItem: T | undefined;
	const result: T[][] = [];

	function _push(g: T[]) {
		if (g.length) {
			result.push(g);
		}
	}

	for (let i = 0, len = arr.length; i < len; i++) {
		lastItem = currentItem ?? arr[i];
		currentItem = arr[i];

		if (!predicate(currentItem, lastItem)) {
			_push(group);
			group = [];
		}

		group.push(currentItem);
	}

	_push(group);

	return result;
}

export function nonNullable<T>(value: T): value is NonNullable<T> {
	return value !== null && value !== undefined;
}

export function findAnchors(obj: ISbComponentType<string> & {[index: string]: any}): AnchorStoryblok[] {
	return Object.values(obj).reduce<AnchorStoryblok[]>((result, value) => {
		if (value && typeof value === 'object' && 'component' in value && value.component === 'Anchor') {
			result.push(value as AnchorStoryblok);
		}

		if (value && typeof value === 'object') {
			result = result.concat(findAnchors(value));
		}

		return result;
	}, []);
}

export function mapDatasourceEntryToSelectboxOption(entry: ISbDatasourceEntry): Option {
	return {
		label: entry.name,
		value: entry.value,
	};
}

export function mapDatasourceEntryFilter(entry: ISbDatasourceEntry): Tag {
	return {
		id: entry.id,
		label: entry.name,
		value: entry.value,
	};
}

export default function mapDotHelper(bounds: IMapBounds) {
	const latRange = bounds.north - bounds.south;
	const lngRange = bounds.east - bounds.west;

	return (coords: IMapCoords): {top: string; left: string} => ({
		top: `${((bounds.north - coords.lat) / latRange) * 100}%`,
		left: `${(1 - (bounds.east - coords.lng) / lngRange) * 100}%`,
	});
}

export function roundToMultiplesOf(value: number, multiple: number, roundFn = Math.round): number {
	return roundFn(value / multiple) * multiple;
}

export function useNullableStringToNumber(input: Ref<string | null>) {
	return useTransformer(
		input,
		(v) => parseInt(v ?? '') || null,
		(v) => v?.toString() ?? null,
	);
}

export function useNullableNumberToString(input: Ref<number | null>) {
	return useTransformer(
		input,
		(v) => v?.toString() ?? null,
		(v) => parseInt(v ?? '') || null,
	);
}

export function useCurrentUrl() {
	if (import.meta.client) {
		return window.location.href;
	}

	return import.meta.server ? useRequestHeaders().referer : '';
}

export function useBaseUrl() {
	if (import.meta.client) {
		return `${window.location.protocol}//${window.location.host}`;
	}

	const headers = useRequestHeaders(['host', 'x-forwarded-host', 'x-forwarded-proto', 'referer']);
	const host = headers['x-forwarded-host'] ?? headers.host;
	if (!host) {
		return '';
	}

	const protocol = headers['x-forwarded-proto']
		? `${headers['x-forwarded-proto']}://`
		: headers.referer?.match(/^(.*?):\/\//)?.[0] ?? (process.env.NODE_ENV === 'development' ? 'http://' : 'https://');

	return `${protocol}${host}`;
}

export function slugToBusiness(slug: string): ProductionTypeIdEnum | null {
	switch (slug) {
		case 'reznictvi':
			return ProductionTypeIdEnum.BUTCHERY;
		case 'pekarna-cukrarna':
			return ProductionTypeIdEnum.BAKERY;
		case 'restaurace':
			return ProductionTypeIdEnum.RESTAURANT;
		case 'catering':
			return ProductionTypeIdEnum.CATERING;
		default:
			return null;
	}
}

export function businessToName(business: ProductionTypeIdEnum | null): string {
	switch (business) {
		case ProductionTypeIdEnum.BUTCHERY:
			return 'Řeznictví';
		case ProductionTypeIdEnum.BAKERY:
			return 'Pekárna & Cukrárna';
		case ProductionTypeIdEnum.RESTAURANT:
			return 'Restaurace';
		case ProductionTypeIdEnum.CATERING:
			return 'Catering';
		default:
			return '';
	}
}

export function storyblokLinktoPath(url: string): string {
	let path: string;

	try {
		path = `/${new URL(url).pathname}`;
	} catch (error) {
		path = `/${url}`;
	}

	return dropUnderscoreRouteParts(path);
}

export function isOutOfStock(id: number | undefined): boolean {
	return id === 3;
}

export {dropUnderscoreRouteParts, ensureLeadingSlash, removeTrailingSlash};

export function formatPrice(price: number | string | null | undefined): string {
	if (price == null || price === '') {
		return '';
	}

	const numeric = Number(price);
	if (Number.isNaN(numeric)) {
		return '';
	}

	const {n} = useI18n();
	return n(numeric, Number.isInteger(numeric) ? 'currency' : 'currencyWithPennies');
}

export function formatPriceWithSuperscript(price: number | null | undefined, decimalClass?: string): string {
	const {n} = useI18n();
	if (price == null || isNaN(Number(price))) {
		return '';
	}
	const [integerPart, decimalPart = '00'] = price.toFixed(2).split('.');
	return `<span aria-hidden='true'>${n(Number(integerPart))}<span><sup class='${decimalClass ?? 'text-[0.6em]'}'>${decimalPart}</sup></span></span>`;
}

export function countDigitsBeforeDecimal(price: number) {
	const priceStr = price.toString();
	const decimalIndex = priceStr.indexOf('.');

	if (decimalIndex !== -1) {
		return decimalIndex;
	} else {
		return priceStr.length;
	}
}

export function isInStock(product: ProductDetail): boolean | undefined {
	return ProductAvailability.getAvailability(product).isAvailable;
}

export function computedWithPrev<T>(cb: (prev: T | undefined) => T): ComputedRef<T> {
	let previous: T | undefined;

	return computed(() => {
		previous = cb(previous);
		return previous;
	});
}

//
// Extrahuje hodnoty z řetězce podle zadaného vzoru.
//
// @param pattern - Vzor, který obsahuje '*' jako zástupný symbol pro extrahované hodnoty.
// @param string - Řetězec, ze kterého se mají hodnoty extrahovat.
// @returns Pole extrahovaných hodnot. Pokud řetězec neodpovídá vzoru, vrátí prázdné pole.
//
// @example
// const pattern = "_globus-cz/_pobocky/*/letaky/*";
// const string = "_globus-cz/_pobocky/praha-zlicin/letaky/akcni-letak-24-01-2024-265ad9e5529c49";
// const values = extractValuesFromPattern(pattern, string); // ["praha-zlicin", "akcni-letak-24-01-2024-265ad9e5529c49"]
export function extractValuesFromPattern(pattern: string, string: string): string[] {
	const regexPattern = pattern.replace(/\*/g, '(.+)');
	const regex = new RegExp(regexPattern);
	const match = string.match(regex);
	return match ? match.slice(1) : [];
}

export function createTreeNode<T>(item: T, children: Tree<T>[]): Tree<T> {
	return {item, children};
}

export function createTreeFromChildren<
	IdKey extends string,
	ChildrenIdsKey extends string,
	T extends Record<IdKey, string> & Partial<Record<ChildrenIdsKey, string[] | null>>,
>(
	items: T[],
	idKey: IdKey,
	childrenIdsKey: ChildrenIdsKey,
	rootIds?: string[],
): Tree<T>[] {
	const itemsMap = new Map<string, T>();
	const hasParent = new Set<string>();

	// Build indexes and track items with parents
	for (const item of items) {
		itemsMap.set(item[idKey], item);

		const childrenIds = item[childrenIdsKey];
		if (Array.isArray(childrenIds)) {
			for (const childId of childrenIds) {
				hasParent.add(childId);
			}
		}
	}

	// Determine root items
	const rootItems = rootIds
		? rootIds.map((id) => itemsMap.get(id)).filter((item): item is T => item !== undefined)
		: items.filter((item) => !hasParent.has(item[idKey]));

	// Shared Set for cycle detection via backtracking
	const pathVisited = new Set<string>();

	// Recursively build the tree
	function buildTree(item: T): Tree<T> {
		const itemId = item[idKey];

		// Add current node to the visited path
		pathVisited.add(itemId);

		const childrenIds = item[childrenIdsKey];
		const children: Tree<T>[] = [];

		if (Array.isArray(childrenIds)) {
			for (const childId of childrenIds) {
				// Prevent infinite loops by skipping cyclic children
				if (pathVisited.has(childId)) {
					continue;
				}

				const childItem = itemsMap.get(childId);
				if (childItem) {
					children.push(buildTree(childItem));
				}
			}
		}

		// Backtracking: Remove node from path before returning
		pathVisited.delete(itemId);

		return createTreeNode(item, children);
	}

	return rootItems.map((item) => buildTree(item));
}

export function watchUntilRouteLeave(source: WatchSource, callback: WatchCallback, options?: WatchOptions) {
	const stop = watch(source, callback, options);
	onBeforeRouteLeave(stop);
	return stop;
}

/**
 * Batch calls to a function and call it with an array of arguments after a certain interval
 * Example:
 * ```
 * const batchedLog = batchCalls(console.log, 1000);
 * batchedLog('a');
 * batchedLog('b');
 * batchedLog('c');
 * // After 1000ms: [['a'], ['b'], ['c']]
 * ```
 */
export function batchCalls<Params extends readonly any[]>(
	callback: (batch: Params[]) => any,
	interval: number,
): (...params: Params) => void {
	let timeout: any = null;
	let args: Params[] = [];

	return (...newArgs) => {
		args.push(newArgs);

		if (!timeout) {
			timeout = setTimeout(() => {
				callback(args);
				args = [];
				timeout = null;
			}, interval);
		}
	};
}

export function simplifyHypermarket(hypermarket: Hypermarket): SimplifiedHypermarket {
	return {
		id: hypermarket.content.gsoaId,
		name: hypermarket.content.name,
		address: hypermarket.content.address,
	};
}

export function generateFrontendErrorId(): string {
	return `fe-${uuidv4()}`;
}

export function getPhoneLink(phone?: string | null): string {
	if (!phone) {
		return '';
	}
	const cleanedNumber = phone.replace(/[^\d+]/g, '');
	return `tel:${cleanedNumber}`;
}
